import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '@/auth/AuthContext';
import { useSiteSettings } from '@/settings/useSiteSettings';
import {
  getMyConversations, createConversation, getMessages, openAndReadMessages, sendMessage,
  markConversationRead, subscribeToMessages, subscribeToConversations,
  subscribeToTyping, subscribeToAllTyping, emitTyping, emitStopTyping, uploadChatFile, checkUnreadConversations,
  type Conversation, type ChatMessage,
} from '@/services/supportChat';

export default function LiveChat() {
  const navigate = useNavigate();
  const { user, profile } = useAuth();
  const { settings } = useSiteSettings();
  const s = settings.support;
  const [open, setOpen] = useState(false);
  const [convs, setConvs] = useState<Conversation[]>([]);
  const [activeConv, setActiveConv] = useState<string | null>(() => localStorage.getItem('chat_active_conv'));
  const [msgs, setMsgs] = useState<ChatMessage[]>([]);
  const [input, setInput] = useState('');
  const [sending, setSending] = useState(false);
  const [subject, setSubject] = useState('');
  const [showNew, setShowNew] = useState(false);
  const [loading, setLoading] = useState(true);
  const [typingWho, setTypingWho] = useState('');
  const [uploading, setUploading] = useState(false);
  const [previewUrl, setPreviewUrl] = useState('');
  const [previewFile, setPreviewFile] = useState<File | null>(null);
  const [fileError, setFileError] = useState('');
  const [lightbox, setLightbox] = useState('');
  const [dragOver, setDragOver] = useState(false);
  const [size, setSize] = useState(() => {
    const w = localStorage.getItem('chat_width');
    const h = localStorage.getItem('chat_height');
    return { width: w ? Number(w) : 400, height: h ? Number(h) : 560 };
  });
  const bottomRef = useRef<HTMLDivElement>(null);
  const fileRef = useRef<HTMLInputElement>(null);
  const dragCounter = useRef(0);
  const resizeRef = useRef<{ startX: number; startY: number; startW: number; startH: number } | null>(null);

  const audioCtxRef = useRef<AudioContext | null>(null);
  const getAudioCtx = () => {
    if (!audioCtxRef.current) {
      audioCtxRef.current = new (window.AudioContext || (window as any).webkitAudioContext)();
    }
    if (audioCtxRef.current.state === 'suspended') {
      audioCtxRef.current.resume();
    }
    return audioCtxRef.current;
  };

  const playNotifSound = () => {
    try {
      const ctx = getAudioCtx();
      const play = (freq: number, time: number, dur: number) => {
        const osc = ctx.createOscillator();
        const gain = ctx.createGain();
        osc.connect(gain);
        gain.connect(ctx.destination);
        osc.frequency.value = freq;
        osc.type = 'sine';
        gain.gain.setValueAtTime(0.12, ctx.currentTime + time);
        gain.gain.exponentialRampToValueAtTime(0.001, ctx.currentTime + time + dur);
        osc.start(ctx.currentTime + time);
        osc.stop(ctx.currentTime + time + dur);
      };
      play(660, 0, 0.12);
      play(880, 0.1, 0.15);
    } catch {}
  };

  // Persist active conversation
  useEffect(() => {
    if (activeConv) localStorage.setItem('chat_active_conv', activeConv);
    else localStorage.removeItem('chat_active_conv');
  }, [activeConv]);

  // Resize handler
  const handleResizeStart = (e: React.MouseEvent) => {
    e.preventDefault();
    resizeRef.current = { startX: e.clientX, startY: e.clientY, startW: size.width, startH: size.height };
    const onMove = (ev: MouseEvent) => {
      if (!resizeRef.current) return;
      const w = Math.max(320, resizeRef.current.startW - (ev.clientX - resizeRef.current.startX));
      const h = Math.max(400, resizeRef.current.startH - (ev.clientY - resizeRef.current.startY));
      setSize({ width: w, height: h });
    };
    const onUp = () => {
      resizeRef.current = null;
      localStorage.setItem('chat_width', String(size.width));
      localStorage.setItem('chat_height', String(size.height));
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
  };

  const handleDragEnter = (e: React.DragEvent) => { e.preventDefault(); dragCounter.current++; setDragOver(true); };
  const handleDragLeave = (e: React.DragEvent) => { e.preventDefault(); dragCounter.current--; if (dragCounter.current === 0) setDragOver(false); };
  const handleDragOver = (e: React.DragEvent) => e.preventDefault();
  const handleDrop = async (e: React.DragEvent) => {
    e.preventDefault(); setDragOver(false); dragCounter.current = 0;
    const file = e.dataTransfer.files[0];
    if (!file || !activeConv) return;
    if (!validateFile(file)) return;
    setUploading(true);
    try { const url = await uploadChatFile(file); await sendMessage(activeConv, `📎 ${file.name}: ${url}`, 'customer'); } catch {}
    setUploading(false);
  };

  const loadConvs = async () => {
    try { const data = await getMyConversations(); setConvs(data); } catch {}
    setLoading(false);
  };

  useEffect(() => {
    if (open && user) {
      loadConvs();
      checkUnreadConversations('customer');
    }
  }, [open, user]);

  useEffect(() => {
    if (!user) return;
    const sub = subscribeToConversations(user.id, (payload: any) => {
      if (payload.eventType === 'INSERT' || payload.new?.unreadCustomer > 0) playNotifSound();
      loadConvs();
      if (activeConv) getMessages(activeConv).then(setMsgs).catch(() => {});
    });
    return () => { try { sub?.unsubscribe(); } catch {} };
  }, [user, activeConv]);

  useEffect(() => {
    if (!activeConv) return;
    const sub = subscribeToMessages(activeConv, (payload: any) => {
      if (payload.new?.sender_role === 'admin') playNotifSound();
      getMessages(activeConv).then(setMsgs).catch(() => {});
    });
    const typingSub = subscribeToTyping(activeConv, 'customer', (who) => setTypingWho(who));
    return () => { try { sub?.unsubscribe(); } catch {}; try { typingSub?.unsubscribe(); } catch {} };
  }, [activeConv]);

  useEffect(() => { bottomRef.current?.scrollIntoView({ behavior: 'smooth' }); }, [msgs]);

  // Load messages for persisted conversation when chat opens
  useEffect(() => {
    if (open && activeConv && msgs.length === 0) {
      getMessages(activeConv, 'customer').then(setMsgs).catch(() => {});
    }
  }, [open, activeConv]);

  const openConv = async (id: string) => {
    setActiveConv(id);
    const data = await openAndReadMessages(id, 'customer');
    setMsgs(data);
    await markConversationRead(id, 'customer');
    setConvs((prev) => prev.map((c) => (c.id === id ? { ...c, unreadCustomer: 0 } : c)));
  };

  const handleInputChange = (val: string) => {
    setInput(val);
    if (activeConv && val.trim()) {
      emitTyping(activeConv, 'customer', profile?.name || 'Customer');
    } else if (activeConv) {
      emitStopTyping(activeConv, 'customer', profile?.name || 'Customer');
    }
  };

  const validateFile = (file: File) => {
    if (file.size > 10 * 1024 * 1024) { setFileError('File too large. Maximum 10MB.'); return false; }
    setFileError('');
    return true;
  };

  const handleFileSelect = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    if (!validateFile(file)) { if (fileRef.current) fileRef.current.value = ''; return; }
    if (file.type.startsWith('image/')) {
      setPreviewUrl(URL.createObjectURL(file));
      setPreviewFile(file);
    } else {
      setPreviewFile(file);
      sendFile(file);
    }
    if (fileRef.current) fileRef.current.value = '';
  };

  const sendFile = async (file: File) => {
    if (!activeConv) return;
    setUploading(true);
    try { const url = await uploadChatFile(file); await sendMessage(activeConv, `📎 ${file.name}: ${url}`, 'customer'); } catch {}
    setUploading(false);
    setPreviewUrl(''); setPreviewFile(null); setFileError('');
  };

  const handleFileUpload = (e: React.ChangeEvent<HTMLInputElement>) => {};

  const handleSend = async () => {
    if (!input.trim() || !activeConv || sending) return;
    emitStopTyping(activeConv, 'customer', profile?.name || 'Customer');
    setSending(true);
    try { await sendMessage(activeConv, input.trim(), 'customer'); setInput(''); } catch {}
    setSending(false);
  };

  const handleNew = async () => {
    if (!subject.trim()) return;
    try {
      const conv = await createConversation(subject);
      setConvs((prev) => [conv, ...prev]);
      setActiveConv(conv.id); setMsgs([]); setShowNew(false); setSubject('');
    } catch {}
  };

  const unreadTotal = convs.reduce((s, c) => s + c.unreadCustomer, 0);

  return (
    <>
      <button
        onClick={() => { getAudioCtx(); setOpen((v) => !v); }}
        className="fixed bottom-6 right-6 z-50 w-14 h-14 rounded-full bg-brand-600 hover:bg-brand-700 text-white shadow-lg flex items-center justify-center transition-all hover:scale-105 active:scale-95">
        {open ? <i className="ri-close-line text-2xl" /> : <i className="ri-chat-3-line text-2xl" />}
        {unreadTotal > 0 && !open && (
          <span className="absolute -top-1 -right-1 w-5 h-5 rounded-full bg-red-500 text-white text-[10px] font-bold flex items-center justify-center shadow">
            {unreadTotal > 9 ? '9+' : unreadTotal}
          </span>
        )}
      </button>

      {open && (
        <div className="fixed bottom-24 right-6 z-50 bg-white rounded-2xl border border-surface-200 shadow-2xl flex flex-col overflow-hidden" style={{ width: size.width, height: size.height }}>
          <div className="absolute top-0 left-0 w-5 h-5 cursor-nw-resize z-20 group" onMouseDown={handleResizeStart}>
            <div className="w-3 h-3 border-t-2 border-l-2 border-surface-300 group-hover:border-brand-500 transition-colors rounded-tl" />
          </div>
          <div className="px-4 py-3 border-b border-surface-100 bg-brand-600 text-white">
            <h3 className="text-sm font-bold">Live Chat</h3>
            <p className="text-[11px] text-white/80">
              {(() => {
                if (!s?.businessHoursEnabled) return 'We usually reply in a few minutes';
                const now = new Date();
                const h = now.getHours().toString().padStart(2, '0');
                const m = now.getMinutes().toString().padStart(2, '0');
                const t = `${h}:${m}`;
                const isOpen = t >= (s.businessHoursStart || '09:00') && t <= (s.businessHoursEnd || '17:00');
                return isOpen ? 'We are online' : (s.awayMessage || 'We are currently away');
              })()}
            </p>
          </div>

          {!user ? (
            <div className="flex flex-col items-center justify-center py-12 px-6 text-center">
              <i className="ri-chat-1-line text-4xl text-surface-300 mb-3" />
              <p className="text-sm font-medium text-surface-600 mb-1">Sign in to chat with us</p>
              <p className="text-xs text-surface-400 mb-4">Create an account or sign in to start a conversation.</p>
              <button onClick={() => navigate('/login')} className="px-5 py-2 rounded-lg bg-brand-600 hover:bg-brand-700 text-white text-sm font-semibold transition-colors">Sign In</button>
            </div>
          ) : !activeConv ? (
            <div className="flex-1 overflow-y-auto p-3 space-y-1">
              {loading ? (
                <div className="flex items-center justify-center py-12"><i className="ri-loader-4-line animate-spin text-surface-300 text-xl" /></div>
              ) : convs.length === 0 && !showNew ? (
                <div className="text-center py-8 text-sm text-surface-400">No conversations yet</div>
              ) : (
                convs.map((c) => (
                  <button key={c.id} onClick={() => openConv(c.id)} className="w-full flex items-center gap-3 px-3 py-2.5 rounded-xl hover:bg-surface-50 text-left transition-colors">
                    <div className="w-8 h-8 rounded-full bg-brand-100 flex items-center justify-center text-brand-600 text-xs font-bold shrink-0"><i className="ri-chat-1-line" /></div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-surface-900 truncate">{c.subject || 'Support'}</p>
                      <p className="text-xs text-surface-400">{c.status === 'closed' ? 'Closed' : `${c.unreadCustomer} unread`}</p>
                    </div>
                    {c.unreadCustomer > 0 && <span className="w-5 h-5 rounded-full bg-brand-600 text-white text-[10px] font-bold flex items-center justify-center">{c.unreadCustomer}</span>}
                  </button>
                ))
              )}
              {showNew && (
                <div className="p-3 space-y-2">
                  <input type="text" value={subject} onChange={(e) => setSubject(e.target.value)} placeholder="How can we help?" className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none" autoFocus />
                  <div className="flex gap-2">
                    <button onClick={() => { setShowNew(false); setSubject(''); }} className="px-3 py-1.5 rounded-lg border border-surface-300 text-xs text-surface-600">Cancel</button>
                    <button onClick={handleNew} disabled={!subject.trim()} className="px-3 py-1.5 rounded-lg bg-brand-600 text-white text-xs font-medium disabled:bg-surface-300">Start Chat</button>
                  </div>
                </div>
              )}
              <div className="pt-2">
                <button onClick={() => setShowNew(true)} className="w-full py-2 rounded-lg border-2 border-dashed border-surface-300 text-sm text-surface-500 hover:border-brand-400 hover:text-brand-600 transition-colors">
                  <i className="ri-add-line mr-1" /> New Conversation
                </button>
              </div>
            </div>
          ) : (
            <div className="flex-1 flex flex-col overflow-hidden">
              <div className="flex items-center gap-2 px-4 py-2 border-b border-surface-100 bg-surface-50 shrink-0">
                <button onClick={() => { setActiveConv(null); setMsgs([]); }} className="text-surface-500 hover:text-surface-700"><i className="ri-arrow-left-s-line text-lg" /></button>
                <span className="text-sm font-medium text-surface-700 truncate flex-1">{convs.find((c) => c.id === activeConv)?.subject || 'Support'}</span>
                <span className="text-[10px] text-surface-400 capitalize">{convs.find((c) => c.id === activeConv)?.status}</span>
              </div>
              <div className="flex-1 overflow-y-auto p-4 space-y-3 relative" onDragEnter={handleDragEnter} onDragOver={handleDragOver} onDragLeave={handleDragLeave} onDrop={handleDrop}>
                {dragOver && (
                  <div className="absolute inset-0 z-10 bg-brand-500/10 border-2 border-dashed border-brand-500 rounded-lg flex items-center justify-center">
                    <div className="bg-white rounded-xl px-6 py-4 shadow-lg text-center">
                      <i className="ri-upload-cloud-2-line text-3xl text-brand-500 mb-2" />
                      <p className="text-sm font-semibold text-surface-900">Drop file to attach</p>
                    </div>
                  </div>
                )}
                {msgs.map((m) => {
                  const isMe = m.senderRole === 'customer';
                  const fileMatch = m.message.match(/^📎\s(.+?):\s(.+)$/);
                  const isFile = !!fileMatch;
                  const ext = isFile ? fileMatch![1].split('.').pop()?.toLowerCase() : '';
                  const isImage = isFile && ['jpg','jpeg','png','gif','webp','svg','avif'].includes(ext || '');
                  return (
                    <div key={m.id} className={`flex ${isMe ? 'justify-end' : 'justify-start'}`}>
                      <div className={`max-w-[80%] px-3.5 py-2.5 rounded-2xl text-sm leading-relaxed ${isMe ? 'bg-brand-600 text-white rounded-br-md' : 'bg-surface-100 text-surface-800 rounded-bl-md'}`}>
                        {isImage ? (
                          <img src={fileMatch![2]} alt={fileMatch![1]} className="max-w-full rounded-lg cursor-pointer" onClick={() => setLightbox(fileMatch![2])} loading="lazy" />
                        ) : isFile ? (
                          <a href={fileMatch![2]} target="_blank" rel="noopener noreferrer" className={`inline-flex items-center gap-2 px-3 py-2 rounded-lg ${isMe ? 'bg-white/20 text-white hover:bg-white/30' : 'bg-white text-surface-700 hover:bg-surface-50 border border-surface-200'} transition-colors`}>
                            <i className="ri-file-line" />
                            <span className="text-sm font-medium">{fileMatch![1]}</span>
                            <i className="ri-download-line text-xs ml-1" />
                          </a>
                        ) : (
                          m.message
                        )}
                        <div className={`flex items-center gap-1 mt-1 ${isMe ? 'justify-end' : 'justify-start'}`}>
                          <p className={`text-[10px] ${isMe ? 'text-white/60' : 'text-surface-400'}`}>{new Date(m.createdAt).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })}</p>
                          {isMe && s?.readReceipts && (
                            <span className={`text-[11px] ${m.seen ? 'text-blue-400' : 'text-white/40'}`}>
                              {m.seen ? <i className="ri-check-double-fill" /> : <i className="ri-check-line" />}
                            </span>
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}
                {typingWho && (
                  <div className="flex items-center gap-2 text-xs text-surface-400 italic">
                    <span className="flex gap-0.5"><span className="w-1 h-1 rounded-full bg-surface-400 animate-bounce" style={{ animationDelay: '0ms' }} /><span className="w-1 h-1 rounded-full bg-surface-400 animate-bounce" style={{ animationDelay: '150ms' }} /><span className="w-1 h-1 rounded-full bg-surface-400 animate-bounce" style={{ animationDelay: '300ms' }} /></span>
                    {typingWho} is typing…
                  </div>
                )}
                <div ref={bottomRef} />
              </div>
              {convs.find((c) => c.id === activeConv)?.status === 'closed' ? (
                <div className="p-3 border-t border-surface-100 text-center text-xs text-surface-400 italic bg-surface-50">This conversation is closed.</div>
              ) : (
              <div className="p-3 border-t border-surface-100 shrink-0">
                {previewUrl && (
                  <div className="flex items-center gap-2 mb-2 p-2 rounded-lg bg-surface-50 border border-surface-200">
                    <img src={previewUrl} alt="" className="w-10 h-10 rounded object-cover" />
                    <span className="text-xs text-surface-600 truncate flex-1">{previewFile?.name}</span>
                    <button onClick={() => sendFile(previewFile!)} disabled={uploading} className="px-2.5 py-1 rounded-lg bg-brand-600 text-white text-xs font-medium disabled:bg-surface-300">{uploading ? 'Uploading…' : 'Send'}</button>
                    <button onClick={() => { setPreviewUrl(''); setPreviewFile(null); }} className="text-surface-400 hover:text-surface-600"><i className="ri-close-line text-sm" /></button>
                  </div>
                )}
                {fileError && <p className="text-[10px] text-red-500 mb-1">{fileError}</p>}
                <div className="flex gap-2">
                  <div className="flex-1 relative">
                    <input type="text" value={input} onChange={(e) => {
                      if ((s?.maxMessageLength || 5000) && e.target.value.length > (s?.maxMessageLength || 5000)) return;
                      handleInputChange(e.target.value);
                    }} onKeyDown={(e) => e.key === 'Enter' && handleSend()} placeholder="Type a message..." className="w-full px-3 py-2 rounded-lg border border-surface-300 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500/20 pr-14" />
                    <span className="absolute right-2 bottom-1.5 text-[10px] text-surface-400">{input.length}/{s?.maxMessageLength || 5000}</span>
                  </div>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading} className="w-9 h-9 rounded-lg border border-surface-300 text-surface-400 hover:text-brand-600 hover:border-brand-400 flex items-center justify-center shrink-0 transition-colors" title="Attach file (max 10MB)">
                    {uploading ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-file-line text-sm" />}
                  </button>
                  <button onClick={handleSend} disabled={!input.trim() || sending} className="w-9 h-9 rounded-lg bg-brand-600 hover:bg-brand-700 disabled:bg-surface-300 text-white flex items-center justify-center shrink-0">
                    {sending ? <i className="ri-loader-4-line animate-spin text-sm" /> : <i className="ri-send-plane-fill text-sm" />}
                  </button>
                  <input ref={fileRef} type="file" onChange={handleFileSelect} className="hidden" />
                </div>
              </div>
              )}
            </div>
          )}
        </div>
      )}

      {lightbox && (
        <div className="fixed inset-0 z-[9999] bg-black/80 backdrop-blur-sm flex items-center justify-center p-4" onClick={() => setLightbox('')}>
          <button className="absolute top-4 right-4 w-10 h-10 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center transition-colors" onClick={() => setLightbox('')}>
            <i className="ri-close-line text-xl" />
          </button>
          <img src={lightbox} alt="" className="max-w-full max-h-full rounded-xl object-contain" onClick={(e) => e.stopPropagation()} />
        </div>
      )}
    </>
  );
}
